import { Link } from "react-router";

import { styled } from "simplydo/react/styled";
import { Icon } from "simplydo/react/ui/icon";

import type { ChipSocialLink } from "./useSocialLinks";

const ROW_LIMIT = 5;
const STACK_LIMIT = 3;

const SocialGlyph = ({ link, px }: { link: ChipSocialLink; px: number }) =>
  link.icon ? (
    <Icon
      icon={link.icon}
      style={{ color: link.color, width: px, height: px }}
    />
  ) : (
    <span style={{ fontSize: px, lineHeight: 1, color: "black" }}>
      {link.glyph}
    </span>
  );

const SocialAnchor = ({
  link,
  className,
  children,
}: {
  link: ChipSocialLink;
  className?: string;
  children: React.ReactNode;
}) =>
  link.internal ? (
    <Link
      to={link.href}
      className={className}
      title={link.label}
      aria-label={link.label}
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </Link>
  ) : (
    <a
      href={link.href}
      className={className}
      title={link.label}
      aria-label={link.label}
      target="_blank"
      rel="noreferrer noopener"
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </a>
  );

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  height: 12px;
  margin-bottom: 2px;
  font-size: 9px;
  font-weight: 700;
  color: #666;

  > a {
    display: flex;
  }
`;

/** Small links above the name on default-size chips. */
export const SocialRow = ({ links }: { links: ChipSocialLink[] }) => {
  const shown = links.slice(
    0,
    links.length > ROW_LIMIT ? ROW_LIMIT - 1 : ROW_LIMIT,
  );
  return (
    <Row>
      {shown.map((link) => (
        <SocialAnchor key={link.key} link={link}>
          <SocialGlyph link={link} px={11} />
        </SocialAnchor>
      ))}
      {links.length > shown.length ? (
        <span>+{links.length - shown.length}</span>
      ) : null}
    </Row>
  );
};

const Stack = styled.div`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  margin-left: 8px;

  > span {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: white;
    box-shadow:
      0 0 0 1.5px white,
      0 1px 3px rgba(0, 0, 0, 0.2);
    font-size: 9px;
    font-weight: 700;
    color: #555;
  }

  > span + span {
    margin-left: -5px;
  }

  &.is-fluid {
    margin-left: auto;
    padding-left: 8px;
  }
`;

/** Overlapping logos at the end of compact chips. The popup holds the links. */
export const SocialStack = ({
  links,
  className,
}: {
  links: ChipSocialLink[];
  className?: string;
}) => {
  const external = links.filter((link) => !link.internal);
  const shown = external.slice(
    0,
    external.length > STACK_LIMIT ? STACK_LIMIT - 1 : STACK_LIMIT,
  );
  if (!shown.length) return null;
  return (
    <Stack
      className={className}
      title={external.map((link) => link.label).join(", ")}
    >
      {shown.map((link) => (
        <span key={link.key}>
          <SocialGlyph link={link} px={11} />
        </span>
      ))}
      {external.length > shown.length ? (
        <span>+{external.length - shown.length}</span>
      ) : null}
    </Stack>
  );
};

const Buttons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;

  > a {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: #f3f5f8;
    transition: background 0.12s;
  }

  > a:hover {
    background: #e6eaf1;
  }
`;

/** Round link buttons for the profile popup. */
export const SocialButtons = ({ links }: { links: ChipSocialLink[] }) => (
  <Buttons>
    {links.map((link) => (
      <SocialAnchor key={link.key} link={link}>
        <SocialGlyph link={link} px={14} />
      </SocialAnchor>
    ))}
  </Buttons>
);
